
import React, { useEffect, useRef, useState } from "react";
import { useToast } from "./Toast";
import { useUser } from "../contexts/UserContext";
import EngineerCard from "./EngineerCard";
import EngineerForm from "./EngineerForm";
import EngineerStats from "./EngineerStats";
import EngineerMemo from "./EngineerMemo";
import CSVImporter from "./CSVImporter";
import PPSalesProgress from "./PPSalesProgress";
import BPProgress from "./BPProgress";
import CompanyAppointmentManager from "./CompanyAppointmentManager";
import PartnerEngineerList from "./PartnerEngineerList";
import {
  PageHeader,
  Button,
  Badge,
  badgeVariantForLabel,
  LoadingState,
  EmptyState,
} from "./ui";

const TAB_META = {
  dashboard: { title: 'エンジニア統計ダッシュボード', description: 'リアルタイム人材管理システム' },
  engineers: { title: 'エンジニア管理', description: null },
  'partner-engineers': { title: 'BPエンジニア', description: 'リアルタイム人材管理システム' },
  'pp-sales': { title: 'PP営業進捗管理', description: 'リアルタイム人材管理システム' },
  'bp-progress': { title: 'BP進捗管理', description: 'リアルタイム人材管理システム' },
  'apo-kanri': { title: 'アポ管理', description: 'リアルタイム人材管理システム' },
};

const PRIMARY_TABS = [
  { key: 'dashboard', icon: 'fa-tachometer-alt', label: 'ダッシュボード' },
  { key: 'engineers', icon: 'fa-users', label: 'IDRエンジニア' },
  { key: 'partner-engineers', icon: 'fa-user-tie', label: 'BPエンジニア' },
];

const SECONDARY_TABS = [
  { key: 'pp-sales', icon: 'fa-handshake', label: 'PP営業進捗' },
  { key: 'bp-progress', icon: 'fa-project-diagram', label: 'BP進捗管理' },
  { key: 'apo-kanri', icon: 'fa-calendar-check', label: 'アポ管理' },
];

const inputClass =
  'w-full px-3 py-2 border border-prodia-border rounded-lg bg-prodia-surface text-sm text-prodia-text-primary placeholder:text-prodia-text-muted focus:border-prodia-primary focus:ring-2 focus:ring-prodia-primary/30 focus:outline-none transition-colors';

// フィールド名の日本語ラベルマッピング
const FIELD_LABELS = {
  name: '名前',
  gender: '性別',
  engineer_status: 'ステータス',
  project_name: '案件名',
  planner: '担当者',
  client_company: 'クライアント企業',
  monthly_rate: '月単価',
  project_start_date: '案件開始日',
  project_end_date: '案件終了日',
};

// 操作ログを DB に記録
function logActivity(action, targetName, userName, details = null) {
  fetch('/api/activity-logs/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action,
      target_type: 'engineer',
      target_name: targetName || '不明',
      user_name: userName || 'システム',
      details: details,
    }),
  }).catch(() => { /* ログ失敗は無視 */ });
}

// モダン・ラグジュアリーヘッダーコンポーネント
export default function EngineerList() {
  const toast = useToast();
  const { user } = useUser();
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEngineer, setEditingEngineer] = useState(null);
  const [search, setSearch] = useState({ status: '', skill: '', planner: '', name: '' });
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [selectedEngineers, setSelectedEngineers] = useState([]);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [displayMode, setDisplayMode] = useState('card'); // 'card' or 'table'（カード/テーブル表示切り替え）
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('prodia_engineer_tab') || 'dashboard'); // タブ切り替え用の状態（初期表示はダッシュボード）
  const [showMemoModal, setShowMemoModal] = useState(false);
  const [selectedEngineerForMemo, setSelectedEngineerForMemo] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // メモ変更時のカード更新トリガー
  const [showCSVImporter, setShowCSVImporter] = useState(false); // CSVインポートモーダル
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 12;
  const scrollContainerRef = useRef(null);
  const pendingScrollToIdRef = useRef(null);

  const fetchEngineers = () => {
    setLoading(true);
    fetch("/api/engineers/")
      .then((res) => {
        if (!res.ok) throw new Error("API error");
        return res.json();
      })
      .then((data) => {
        setEngineers(data);
        setLoading(false);
        if (pendingScrollToIdRef.current) {
          const targetId = pendingScrollToIdRef.current;
          pendingScrollToIdRef.current = null;
          setTimeout(() => {
            const el = document.getElementById(`engineer-card-${targetId}`);
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        }
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchEngineers();
  }, []);

  useEffect(() => {
    localStorage.setItem('prodia_engineer_tab', activeTab);
  }, [activeTab]);

  // 検索フィルタ + ソート
  const nameSuggestions = search.name
    ? engineers
        .filter(e => e.name && e.name.includes(search.name))
        .map(e => e.name)
        .filter((v, i, a) => a.indexOf(v) === i)
        .slice(0, 8)
    : [];

  const filtered = engineers.filter(e => {
    const statusMatch = search.status ? (e.engineer_status === search.status) : true;
    const skillMatch = search.skill ? (e.skills && e.skills.some(skill => skill.toLowerCase().includes(search.skill.toLowerCase()))) : true;
    const plannerMatch = search.planner ? (e.planner && e.planner.toLowerCase().includes(search.planner.toLowerCase())) : true;
    const nameMatch = search.name ? (e.name && e.name.includes(search.name)) : true;
    return statusMatch && skillMatch && plannerMatch && nameMatch;
  }).sort((a, b) => {
    let aValue, bValue;
    switch(sortBy) {
      case 'name':
        aValue = a.name;
        bValue = b.name;
        break;
      case 'skills':
        aValue = Array.isArray(a.skills) ? a.skills.length : 0;
        bValue = Array.isArray(b.skills) ? b.skills.length : 0;
        break;
      case 'status':
        aValue = a.engineer_status;
        bValue = b.engineer_status;
        break;
      default:
        aValue = a.name;
        bValue = b.name;
    }
    
    if (typeof aValue === 'string') {
      const comparison = aValue.localeCompare(bValue);
      return sortOrder === 'asc' ? comparison : -comparison;
    } else {
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    }
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // フィルター変更時にページをリセット
  useEffect(() => { setCurrentPage(1); }, [search, sortBy, sortOrder]);

  // ページ変更時にスクロールをトップへ戻す
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage]);

  // 新規登録・編集
  const handleSubmit = async (form, continueAfter = false) => {
    const url = editingEngineer 
      ? `/api/engineers/${editingEngineer.id}/`
      : "/api/engineers/";
    
    const method = editingEngineer ? "PUT" : "POST";
    
    try {
      console.log('送信データ:', {
        ...form,
        skills: form.skills,
        phase: form.phase,
      });

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          skills: form.skills,
          phase: form.phase,
        })
      });

      console.log('レスポンス状態:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('エラー詳細:', errorText);
        
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorData.error || JSON.stringify(errorData);
        } catch (e) {
          errorMessage = errorText || `HTTP ${response.status}: ${response.statusText}`;
        }
        
        throw new Error(`${editingEngineer ? "更新" : "登録"}に失敗しました: ${errorMessage}`);
      }

      await response.json();

      // 更新時の変更フィールドを検知
      let details = null;
      if (editingEngineer) {
        const changes = [];
        const checkFields = [
          'name', 'gender', 'engineer_status', 'project_name', 'planner',
          'client_company', 'monthly_rate', 'project_start_date', 'project_end_date'
        ];
        checkFields.forEach(field => {
          const oldVal = String(editingEngineer[field] ?? '');
          const newVal = String(form[field] ?? '');
          if (oldVal !== newVal) {
            const label = FIELD_LABELS[field] || field;
            changes.push(`${label}: ${oldVal || '(未設定)'} → ${newVal || '(未設定)'}`);
          }
        });
        // スキルの変更
        const added = (form.skills || []).filter(s => !(editingEngineer.skills || []).includes(s));
        const removed = (editingEngineer.skills || []).filter(s => !(form.skills || []).includes(s));
        if (added.length) changes.push(`スキル追加: ${added.join(', ')}`);
        if (removed.length) changes.push(`スキル削除: ${removed.join(', ')}`);
        // フェーズの変更
        const oldPhase = (editingEngineer.phase || []).sort().join(',');
        const newPhase = (form.phase || []).sort().join(',');
        if (oldPhase !== newPhase) {
          changes.push(`工程: ${(editingEngineer.phase || []).join('・') || '(未設定)'} → ${(form.phase || []).join('・') || '(未設定)'}`);
        }
        if (changes.length > 0) details = changes.join('、');
      }

      logActivity(editingEngineer ? 'update' : 'create', form.name, user?.name, details);
      
      // 連続登録でなければフォームを閉じる
      if (!continueAfter) {
        setShowForm(false);
        setEditingEngineer(null);
      }
      
      // 更新時はスクロール位置を記憶してから再取得
      if (editingEngineer) {
        pendingScrollToIdRef.current = editingEngineer.id;
      }
      fetchEngineers();
      
      // 成功メッセージ
      toast.success(
        continueAfter 
          ? "登録完了！続けて登録できます" 
          : (editingEngineer ? "更新完了!" : "登録完了!") 
      );
      
    } catch (err) {
      throw err; // エラーをフォームに返す
    }
  };

  // 編集開始
  const handleEdit = (engineer) => {
    setEditingEngineer(engineer);
    setShowForm(true);
  };

  // メモ表示
  const handleMemoClick = (engineerName) => {
    setSelectedEngineerForMemo(engineerName);
    setShowMemoModal(true);
  };

  // メモモーダルを閉じる（メモ変更時のリアルタイム更新対応）
  const closeMemoModal = () => {
    setShowMemoModal(false);
    setSelectedEngineerForMemo(null);
    // モーダルを閉じる際にも最終的にカードを更新（念のため）
    setRefreshTrigger(prev => prev + 1);
  };

  // メモ変更時のコールバック（EngineerMemoから呼び出される）
  const handleMemoChange = () => {
    // メモが変更された際にエンジニアカードのメモ統計をリアルタイム更新
    setRefreshTrigger(prev => prev + 1); // トリガー値を変更してカードのuseEffectを実行
  };

  // 削除
  const handleDelete = (engineerId) => {
    const target = engineers.find(e => e.id === engineerId);
    fetch(`/api/engineers/${engineerId}/`, {
      method: "DELETE",
    })
      .then(res => {
        if (!res.ok) throw new Error("削除に失敗しました");
        logActivity('delete', target?.name || '不明', user?.name);
        fetchEngineers();
        toast.success("削除完了!");
      })
      .catch(err => toast.error(err.message));
  };

  // フォームのキャンセル
  const handleCancel = () => {
    setShowForm(false);
    setEditingEngineer(null);
  };

  // CSVインポート関連
  const handleCSVImport = () => {
    setShowCSVImporter(true);
  };

  const handleCSVImportComplete = (result) => {
    toast.success(
      `${result.created_count}名のエンジニアを登録しました${
        result.skipped_count > 0 ? `（${result.skipped_count}名は重複のためスキップ）` : ''
      }`
    );
    fetchEngineers(); // データを再取得
  };

  const handleCSVImportClose = () => {
    setShowCSVImporter(false);
  };

  // 一括操作
  const handleSelectEngineer = (engineerId, isSelected) => {
    if (isSelected) {
      setSelectedEngineers([...selectedEngineers, engineerId]);
    } else {
      setSelectedEngineers(selectedEngineers.filter(id => id !== engineerId));
    }
  };

  const handleSelectAll = () => {
    if (selectedEngineers.length === filtered.length) {
      setSelectedEngineers([]);
    } else {
      setSelectedEngineers(filtered.map(e => e.id));
    }
  };

  const handleBulkDelete = () => {
    if (selectedEngineers.length === 0) return;
    
    if (window.confirm(`選択した${selectedEngineers.length}件のエンジニアを削除しますか？`)) {
      Promise.all(
        selectedEngineers.map(id => 
          fetch(`/api/engineers/${id}/`, { method: "DELETE" })
        )
      ).then(() => {
        fetchEngineers();
        setSelectedEngineers([]);
        toast.success(`${selectedEngineers.length}件のエンジニアを削除しました`);
      }).catch(err => toast.error("一括削除に失敗しました"));
    }
  };

  const handleBulkStatusChange = (newStatus) => {
    if (selectedEngineers.length === 0) return;
    
    Promise.all(
      selectedEngineers.map(id => {
        const engineer = engineers.find(e => e.id === id);
        return fetch(`/api/engineers/${id}/`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...engineer, engineer_status: newStatus })
        });
      })
    ).then(() => {
      fetchEngineers();
      setSelectedEngineers([]);
      toast.success(`${selectedEngineers.length}件のステータスを更新しました`);
    }).catch(err => toast.error("一括更新に失敗しました"));
  };

  // CSV エクスポート
  const handleExportCSV = () => {
    if (filtered.length === 0) { toast.error("出力するデータがありません"); return; }
    const csvData = filtered.map(e => ({
      name: e.name || '',
      position: e.position || '',
      project_name: e.project_name || '',
      planner: e.planner || '',
      skills: Array.isArray(e.skills) ? e.skills.join(',') : (e.skills || ''),
      engineer_status: e.engineer_status || '',
      phase: Array.isArray(e.phase) ? e.phase.join(',') : (e.phase || ''),
      client_company: e.client_company || '',
      monthly_rate: e.monthly_rate || '',
      project_start_date: e.project_start_date || '',
      project_end_date: e.project_end_date || '',
      project_location: e.project_location || '',
    }));

    const headers = Object.keys(csvData[0]);
    const escape = (val) => `"${String(val).replace(/"/g, '""')}"`;
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => headers.map(h => escape(row[h])).join(','))
    ].join('\n');

    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `engineers_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success("CSVファイルをダウンロードしました");
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full soft-page">
        <LoadingState message="エンジニアデータを読み込み中..." className="flex-1 min-h-[50vh]" />
      </div>
    );
  }
  if (error) return (
    <div className="flex flex-col h-full soft-page items-center justify-center p-6">
      <div className="max-w-md w-full bg-prodia-surface border border-prodia-border rounded-xl p-8 text-center">
        <div className="w-12 h-12 rounded-xl bg-danger-bg border border-danger-border flex items-center justify-center mx-auto mb-4">
          <i className="fas fa-exclamation-triangle text-danger text-lg"></i>
        </div>
        <h2 className="text-lg font-semibold text-prodia-text-primary mb-2">エラーが発生しました</h2>
        <p className="text-sm text-prodia-text-secondary mb-6">{error}</p>
        <Button variant="danger" onClick={() => window.location.reload()}>
          ページを再読み込み
        </Button>
      </div>
    </div>
  );

  const tabMeta = TAB_META[activeTab] || TAB_META.engineers;
  const pageDescription =
    activeTab === 'engineers' ? `登録数: ${engineers.length}名` : tabMeta.description;

  const tabNav = (
    <div className="flex items-center bg-prodia-background rounded-lg p-1 border border-prodia-border gap-0.5 flex-wrap">
      {PRIMARY_TABS.map(tab => (
        <button
          key={tab.key}
          onClick={() => setActiveTab(tab.key)}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors duration-150 flex items-center gap-1.5 whitespace-nowrap ${
            activeTab === tab.key
              ? 'bg-prodia-primary text-prodia-primary-foreground'
              : 'text-prodia-text-secondary hover:text-prodia-text-primary hover:bg-prodia-surface'
          }`}
        >
          <i className={`fas ${tab.icon} text-[10px]`}></i>
          {tab.label}
        </button>
      ))}
      <div className="w-px h-5 bg-prodia-border mx-0.5 shrink-0"></div>
      {SECONDARY_TABS.map(tab => (
        <button
          key={tab.key}
          onClick={() => setActiveTab(tab.key)}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors duration-150 flex items-center gap-1.5 whitespace-nowrap ${
            activeTab === tab.key
              ? 'bg-prodia-primary text-prodia-primary-foreground'
              : 'text-prodia-text-secondary hover:text-prodia-text-primary hover:bg-prodia-surface'
          }`}
        >
          <i className={`fas ${tab.icon} text-[10px]`}></i>
          {tab.label}
        </button>
      ))}
    </div>
  );

  return (
    <div
      className="flex flex-col h-full soft-page"
    >
      <div className="soft-noise"></div>

      {/* ページヘッダー */}
      <PageHeader
        title={tabMeta.title}
        description={pageDescription}
        actions={tabNav}
        className="relative z-10"
      />
      <div ref={scrollContainerRef} className="relative z-10 flex-1 overflow-auto px-6 py-5">

        {/* ダッシュボード表示 - エンジニア統計 */}
        {activeTab === 'dashboard' && (
          <div>
            <EngineerStats engineers={engineers} />
          </div>
        )}

        {/* PP営業進捗表示 */}
        {activeTab === 'pp-sales' && (
          <div>
            <PPSalesProgress />
          </div>
        )}

        {/* BP進捗表示 */}
        {activeTab === 'bp-progress' && (
          <div>
            <BPProgress />
          </div>
        )}

        {/* アポ管理表示 */}
        {activeTab === 'apo-kanri' && (
          <div>
            <CompanyAppointmentManager />
          </div>
        )}

        {/* パートナーエンジニアリスト表示 */}
        {activeTab === 'partner-engineers' && (
          <div>
            <PartnerEngineerList />
          </div>
        )}

        {/* エンジニアリスト表示 */}
        {activeTab === 'engineers' && (
          <>

      {/* 一括操作バー */}
      {selectedEngineers.length > 0 && (
        <div className="mb-6 p-4 bg-prodia-surface rounded-xl border border-prodia-border">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-prodia-primary rounded-full animate-pulse"></span>
              <span className="text-prodia-text-primary font-medium text-sm">
                <i className="fas fa-check-square mr-2 text-prodia-primary"></i>
                {selectedEngineers.length}件選択中
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button variant="danger" size="sm" onClick={handleBulkDelete}>
                <i className="fas fa-trash"></i>
                一括削除
              </Button>
              <Button variant="secondary" size="sm" onClick={() => handleBulkStatusChange('アサイン済')}>
                <i className="fas fa-check"></i>
                アサイン済に
              </Button>
              <Button variant="secondary" size="sm" onClick={() => handleBulkStatusChange('未アサイン')}>
                <i className="fas fa-clock"></i>
                未アサインに
              </Button>
            </div>

            <Button variant="ghost" size="sm" onClick={() => setSelectedEngineers([])} aria-label="選択解除">
              <i className="fas fa-times"></i>
            </Button>
          </div>
        </div>
      )}

      {/* タイトル & アクション */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6 gap-4">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-prodia-text-primary flex items-center gap-3">
            <div className="w-9 h-9 bg-prodia-primary rounded-lg flex items-center justify-center">
              <i className="fas fa-users text-prodia-primary-foreground text-sm"></i>
            </div>
            IDRエンジニアリスト
          </h2>
          <div className="flex items-center gap-2 ml-12 flex-wrap">
            <Badge variant="primary">{filtered.length}件表示中</Badge>
            <span className="text-sm text-prodia-text-secondary">/ 総数 {engineers.length}件</span>
            {totalPages > 1 && (
              <span className="text-sm text-prodia-text-muted">{currentPage} / {totalPages}ページ</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* 表示モード切り替え */}
          <div className="flex bg-prodia-background rounded-lg p-1 border border-prodia-border">
            <button
              onClick={() => setDisplayMode('card')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
                displayMode === 'card'
                  ? 'bg-prodia-surface text-prodia-text-primary border border-prodia-border shadow-sm'
                  : 'text-prodia-text-secondary hover:text-prodia-text-primary hover:bg-prodia-surface'
              }`}
            >
              <i className="fas fa-th"></i>
              カード
            </button>
            <button
              onClick={() => setDisplayMode('table')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
                displayMode === 'table'
                  ? 'bg-prodia-surface text-prodia-text-primary border border-prodia-border shadow-sm'
                  : 'text-prodia-text-secondary hover:text-prodia-text-primary hover:bg-prodia-surface'
              }`}
            >
              <i className="fas fa-list"></i>
              テーブル
            </button>
          </div>

          <Button variant="secondary" size="sm" onClick={handleCSVImport}>
            <i className="fas fa-file-import"></i>
            CSVインポート
          </Button>

          <Button variant="secondary" size="sm" onClick={handleExportCSV}>
            <i className="fas fa-file-csv"></i>
            CSV出力
          </Button>

          <Button size="sm" onClick={() => setShowForm(true)}>
            <i className="fas fa-plus"></i>
            新規登録
          </Button>
        </div>
      </div>
      {/* 検索フィルタ + ソート */}
      <div className="soft-panel mb-6 p-5 rounded-xl border border-prodia-border bg-prodia-surface">
        <div className="flex flex-col gap-4">
          {/* 全選択 + フィルター行 */}
          <div className="flex items-center gap-4 flex-wrap">
            {/* 全選択チェックボックス */}
            <label className="flex items-center gap-2 cursor-pointer group shrink-0">
              <input
                type="checkbox"
                checked={selectedEngineers.length === filtered.length && filtered.length > 0}
                onChange={handleSelectAll}
                className="w-4 h-4 text-prodia-primary rounded focus:ring-2 focus:ring-prodia-primary/30 border-prodia-border bg-prodia-surface transition-colors"
              />
              <span className="text-sm font-medium text-prodia-text-secondary">全選択</span>
            </label>

            {/* フィルター群 */}
            <div className="flex flex-1 flex-wrap gap-3">
              {/* 名前検索 */}
              <div className="relative min-w-[140px] flex-1">
                <label className="block text-xs font-medium text-prodia-text-secondary mb-1">名前</label>
                <div className="relative">
                  <i className="fas fa-user absolute left-3 top-1/2 -translate-y-1/2 text-prodia-text-muted text-xs"></i>
                  <input
                    className={`${inputClass} pl-8`}
                    value={search.name}
                    onChange={e => {
                      setSearch(s => ({ ...s, name: e.target.value }));
                      setShowNameSuggestions(true);
                    }}
                    onFocus={() => setShowNameSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowNameSuggestions(false), 150)}
                    placeholder="例: 山田"
                  />
                  {showNameSuggestions && nameSuggestions.length > 0 && (
                    <ul className="absolute z-50 top-full left-0 right-0 mt-1 bg-prodia-surface border border-prodia-border rounded-lg shadow-sm overflow-hidden">
                      {nameSuggestions.map(name => (
                        <li
                          key={name}
                          className="px-3 py-2 text-sm text-prodia-text-primary hover:bg-prodia-background cursor-pointer transition-colors"
                          onMouseDown={() => {
                            setSearch(s => ({ ...s, name }));
                            setShowNameSuggestions(false);
                          }}
                        >
                          {name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* アサイン状況 */}
              <div className="min-w-[120px] flex-1">
                <label className="block text-xs font-medium text-prodia-text-secondary mb-1">アサイン状況</label>
                <select
                  className={inputClass}
                  value={search.status}
                  onChange={e => setSearch(s => ({ ...s, status: e.target.value }))}
                >
                  <option value="">すべて</option>
                  <option value="アサイン済">アサイン済</option>
                  <option value="未アサイン">未アサイン</option>
                </select>
              </div>

              {/* スキル検索 */}
              <div className="min-w-[140px] flex-1">
                <label className="block text-xs font-medium text-prodia-text-secondary mb-1">スキル（部分一致）</label>
                <div className="relative">
                  <i className="fas fa-code absolute left-3 top-1/2 -translate-y-1/2 text-prodia-text-muted text-xs"></i>
                  <input
                    className={`${inputClass} pl-8`}
                    value={search.skill}
                    onChange={e => setSearch(s => ({ ...s, skill: e.target.value }))}
                    placeholder="例: Python, React"
                  />
                </div>
              </div>

              {/* プランナー検索 */}
              <div className="min-w-[130px] flex-1">
                <label className="block text-xs font-medium text-prodia-text-secondary mb-1">担当プランナー</label>
                <div className="relative">
                  <i className="fas fa-user-tie absolute left-3 top-1/2 -translate-y-1/2 text-prodia-text-muted text-xs"></i>
                  <input
                    className={`${inputClass} pl-8`}
                    value={search.planner}
                    onChange={e => setSearch(s => ({ ...s, planner: e.target.value }))}
                    placeholder="例: 佐藤"
                  />
                </div>
              </div>

              {/* 並び順 */}
              <div className="min-w-[150px] flex-1">
                <label className="block text-xs font-medium text-prodia-text-secondary mb-1">並び順</label>
                <div className="flex gap-2">
                  <select
                    className={`${inputClass} flex-1`}
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value)}
                  >
                    <option value="name">名前</option>
                    <option value="skills">スキル数</option>
                    <option value="status">ステータス</option>
                  </select>
                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="px-3 py-2 border border-prodia-border rounded-lg hover:border-prodia-primary hover:bg-prodia-background transition-colors flex items-center justify-center text-prodia-text-secondary bg-prodia-surface focus:outline-none focus:ring-2 focus:ring-prodia-primary/30"
                    title={sortOrder === 'asc' ? '昇順' : '降順'}
                  >
                    <i className={`fas text-sm ${sortOrder === 'asc' ? 'fa-sort-amount-up' : 'fa-sort-amount-down'}`}></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* アクティブフィルターの表示 */}
          {(search.status || search.skill || search.planner || search.name) && (
            <div className="pt-3 border-t border-prodia-border">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-xs font-medium text-prodia-text-secondary">フィルター中:</span>
                {search.name && (
                  <Badge variant="info">名前: &quot;{search.name}&quot;</Badge>
                )}
                {search.status && (
                  <Badge variant={badgeVariantForLabel(search.status)}>{search.status}</Badge>
                )}
                {search.skill && (
                  <Badge variant="neutral">スキル: &quot;{search.skill}&quot;</Badge>
                )}
                {search.planner && (
                  <Badge variant="neutral">プランナー: &quot;{search.planner}&quot;</Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearch({ status: '', skill: '', planner: '', name: '' })}
                >
                  <i className="fas fa-times"></i>
                  クリア
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* エンジニア一覧 */}
      {displayMode === 'table' ? (
        /* ── テーブル表示 ── */
        <div className="bg-prodia-surface rounded-xl border border-prodia-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm prodia-table">
              <thead>
                <tr className="border-b border-prodia-border">
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedEngineers.length === filtered.length && filtered.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-prodia-primary rounded focus:ring-2 focus:ring-prodia-primary/30 border-prodia-border"
                    />
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-prodia-text-secondary whitespace-nowrap">名前</th>
                  <th className="px-4 py-3 text-left font-semibold text-prodia-text-secondary whitespace-nowrap">役職</th>
                  <th className="px-4 py-3 text-left font-semibold text-prodia-text-secondary whitespace-nowrap">ステータス</th>
                  <th className="px-4 py-3 text-left font-semibold text-prodia-text-secondary whitespace-nowrap">スキル</th>
                  <th className="px-4 py-3 text-left font-semibold text-prodia-text-secondary whitespace-nowrap">プランナー</th>
                  <th className="px-4 py-3 text-left font-semibold text-prodia-text-secondary whitespace-nowrap">プロジェクト</th>
                  <th className="px-4 py-3 text-center font-semibold text-prodia-text-secondary whitespace-nowrap">操作</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <EmptyState
                        icon="fas fa-search"
                        title="該当するエンジニアがいません"
                        description="検索条件を変更してみてください"
                      />
                    </td>
                  </tr>
                ) : paginated.map((e) => {
                  const skillsArr = Array.isArray(e.skills) ? e.skills : (e.skills || '').split(',').map(s => s.trim()).filter(Boolean);
                  return (
                    <tr key={e.id} className={`transition-colors ${selectedEngineers.includes(e.id) ? 'bg-prodia-primary-subtle/40' : ''}`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedEngineers.includes(e.id)}
                          onChange={(ev) => handleSelectEngineer(e.id, ev.target.checked)}
                          className="w-4 h-4 text-prodia-primary rounded focus:ring-2 focus:ring-prodia-primary/30 border-prodia-border"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-prodia-primary-subtle border border-prodia-border flex items-center justify-center text-prodia-primary text-xs font-bold flex-shrink-0">
                            {e.name?.charAt(0)}
                          </div>
                          <span className="font-semibold text-prodia-text-primary whitespace-nowrap">{e.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-prodia-text-secondary whitespace-nowrap">{e.position || '―'}</td>
                      <td className="px-4 py-3">
                        <Badge variant={badgeVariantForLabel(e.engineer_status)} showIcon>
                          {e.engineer_status || '不明'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {skillsArr.slice(0, 4).map((s, i) => (
                            <Badge key={i} variant="info" className="text-[10px] px-1.5 py-0">{s}</Badge>
                          ))}
                          {skillsArr.length > 4 && <span className="text-[10px] text-prodia-text-muted">+{skillsArr.length - 4}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-prodia-text-secondary whitespace-nowrap">{e.planner || '―'}</td>
                      <td className="px-4 py-3 text-prodia-text-secondary max-w-[160px] truncate">{e.project_name || '―'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => handleMemoClick(e.name)}
                            className="w-7 h-7 rounded-lg bg-prodia-background hover:bg-prodia-primary-subtle text-prodia-primary flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-prodia-primary/30"
                            title="メモ"
                          >
                            <i className="fas fa-sticky-note text-xs"></i>
                          </button>
                          <button
                            onClick={() => handleEdit(e)}
                            className="w-7 h-7 rounded-lg bg-prodia-background hover:bg-info-bg text-info flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-prodia-primary/30"
                            title="編集"
                          >
                            <i className="fas fa-pen text-xs"></i>
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`${e.name}さんを削除しますか？`)) handleDelete(e.id);
                            }}
                            className="w-7 h-7 rounded-lg bg-prodia-background hover:bg-danger-bg text-danger flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-prodia-primary/30"
                            title="削除"
                          >
                            <i className="fas fa-trash text-xs"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* ── カード表示 ── */
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 items-stretch">
          {paginated.map((e, index) => (
          <div 
            key={e.id}
            id={`engineer-card-${e.id}`}
            className="h-full"
          >
            <EngineerCard 
              engineer={{ ...e, project: e.project_name, project_location: e.project_location }}
              onEdit={handleEdit}
              onDelete={handleDelete}
              isSelected={selectedEngineers.includes(e.id)}
              onSelect={handleSelectEngineer}
              onMemoClick={handleMemoClick}
              refreshTrigger={refreshTrigger}
            />
          </div>
        ))}
        
          {/* 空の状態 */}
          {paginated.length === 0 && filtered.length === 0 && (
            <div className="col-span-full">
              <EmptyState
                icon="fas fa-search"
                title="該当するエンジニアがいません"
                description="検索条件を変更してみてください"
                actionLabel="検索をクリア"
                onAction={() => setSearch({ status: '', skill: '', planner: '', name: '' })}
              />
            </div>
          )}
        </div>
      )}

      {/* ページネーション */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="w-9 h-9 p-0"
            aria-label="最初のページ"
          >
            <i className="fas fa-angle-double-left text-xs"></i>
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="w-9 h-9 p-0"
            aria-label="前のページ"
          >
            <i className="fas fa-angle-left text-xs"></i>
          </Button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
            .reduce((acc, p, idx, arr) => {
              if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
              acc.push(p);
              return acc;
            }, [])
            .map((item, idx) =>
              item === '...' ? (
                <span key={`ellipsis-${idx}`} className="w-9 h-9 flex items-center justify-center text-prodia-text-muted text-sm">…</span>
              ) : (
                <button
                  key={item}
                  onClick={() => setCurrentPage(item)}
                  className={`w-9 h-9 rounded-lg text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-prodia-primary/30 ${
                    currentPage === item
                      ? 'bg-prodia-primary text-prodia-primary-foreground'
                      : 'bg-prodia-surface border border-prodia-border text-prodia-text-secondary hover:bg-prodia-background hover:border-prodia-primary/40'
                  }`}
                >
                  {item}
                </button>
              )
            )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="w-9 h-9 p-0"
            aria-label="次のページ"
          >
            <i className="fas fa-angle-right text-xs"></i>
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className="w-9 h-9 p-0"
            aria-label="最後のページ"
          >
            <i className="fas fa-angle-double-right text-xs"></i>
          </Button>
        </div>
      )}
      
      {/* 新規登録・編集モーダル */}
      {showForm && (
        <EngineerForm 
          onSubmit={handleSubmit} 
          onCancel={handleCancel}
          initialData={editingEngineer}
        />
      )}

      {/* メモモーダル */}
      {showMemoModal && selectedEngineerForMemo && (
        <EngineerMemo
          engineerName={selectedEngineerForMemo}
          onClose={closeMemoModal}
          onMemoChange={handleMemoChange}
        />
      )}

      {/* CSVインポートモーダル */}
      {showCSVImporter && (
        <CSVImporter
          onImport={handleCSVImportComplete}
          onClose={handleCSVImportClose}
        />
      )}

      </>
      )}
      </div>
  </div>
  );
}
